import Axios from 'axios'
import { fetchAuthSession as AmplifyFetchAuthSession } from 'aws-amplify/auth'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

export const AuthSession = {
  fetchAuthSession: AmplifyFetchAuthSession,
};

export const axiosInstance = Axios.create()
export const baseSharedAxios = Axios.create()

/**
 * Attach the request and response interceptors to an Axios instance.
 * @param {import('axios').AxiosInstance} instance
 * @param {Object} interceptorConfig
 * @param {import('vue-router').Router} router
 */
function configureAxiosInstance (instance, interceptorConfig, router) {
  instance.interceptors.request.use(requestInterceptor(interceptorConfig));
  instance.interceptors.response.use(
    response => response,
    responseErrorInterceptor(instance, router)
  );
}

/**
 * Determine stage by SSO provider from the request URL.
 * @param {string} request URL
 * @param {Record<string, string>} apiBaseUrls mapping from stage name to base URL for the backend API of each stage
 * @returns {string | null} stage name or null if not applicable
 */
export const stageBySSOProvider = (url, apiBaseUrls) => {
  if (!url.includes('/sso/')) {
    return null;
  }

  // extract SSO Provider name from the URL
  const [, providerName] = url.split('/sso/', 2);

  if (providerName.includes('/')) {
    return null;
  }

  const residency = providerName.match(/^([a-z]{2}):.+$/)?.[1];
  if (!residency) {
    return null;
  }

  return Object
    .keys(apiBaseUrls)
    .find(key => key.endsWith(`-${residency}`)) ?? null;
}

/**
 * If the URL in the Axios request configuration matches the placeholder URL
 * then it replaces it by the stage URL.
 * @param {Record<string, string>} config Axios request configuration object
 * @param {string} placeholderUrl URL to replace
 * @param {string} stageApiUrl URL to replace with
 * @returns {boolean} true if the URL was replaced, false otherwise
 */
function applyStageUrl (config, placeholderUrl, stageApiUrl) {
  let matched = false;
  if (stageApiUrl) {
    if ((config.allowAbsoluteUrls || !config.baseURL) && config.url.startsWith(placeholderUrl)) {
      config.url = stageApiUrl + config.url.substring(placeholderUrl.length);
      matched = true;
    }
    if (config.baseURL === placeholderUrl) {
      matched = true;
      config.baseURL = stageApiUrl;
    }
  }
  return matched;
}

/**
 * Interceptor to modify the request configuration with the correct
 * stage URL.
 */
export const requestInterceptor = ({
  GLOBAL_STAGE,
  API_BASE_URL,
  API_BASE_URLS,
  API_FEEDS_URL,
  API_FEEDS_URLS
}) => {
  return async (config) => {
    let amplifySession;
    try {
      amplifySession = useCognitoAuthStore?.()?.fetchAndStoreAuthSession
        ? await useCognitoAuthStore().fetchAndStoreAuthSession()
        : await AuthSession.fetchAuthSession();
    } catch {
      // Missing/expired auth session is valid for pre-login flows (for example SSO discovery).
      amplifySession = null;
    }

    const jwToken = amplifySession?.tokens?.idToken?.toString()
    let stage = amplifySession?.tokens?.idToken?.payload?.['custom:group_stage'] ?? GLOBAL_STAGE;

    stage = stageBySSOProvider(config.url, API_BASE_URLS) ?? stage;
    if (jwToken) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = jwToken;
    } else if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }

    applyStageUrl(config, API_BASE_URL, API_BASE_URLS[stage]) || applyStageUrl(config, API_FEEDS_URL, API_FEEDS_URLS[stage]);

    return config
  }
}

/**
 * Handle two use cases: one is to redirect the user to the account suspended page
 * if account is suspended, and the second is to retry the request if it is retryable.
 */
export const responseErrorInterceptor = (axios, router) => {
  return async (error) => {
    // Handle Forbidden account suspended error redirection
    const errorStatus = error.response?.status
    const originalRequest = error.config

    const errorException = error.response?.data?.exception ?? error.response?.data?.name
    if (errorStatus === 403 && errorException === 'AccountSuspendedException') {
      // Handle AccountSuspendedException error redirection
      await router.aryaSuspendedRedirection()
    } else if (errorStatus === 403 && errorException === 'ProductNotEnabledException') {
      // Handle ProductNotEnabledException error redirection
      await router.noProductAccessRedirection()
    } else
      if (error.response?.data?.retryable) {
        // FIXME: use axios-retry instead of custom retry logic
        return axios.request(originalRequest)
      }

    return Promise.reject(error)
  }
}

/**
 * Plugin to install Axios with request and response interceptors.
 * It provides the Axios instance to the Vue app and the dependency manager.
 */
const AxiosPlugin = {
  install (app, {
    GLOBAL_STAGE,
    API_BASE_URL,
    API_BASE_URLS,
    API_FEEDS_URL,
    API_FEEDS_URLS,
    router,
    Dependencies
  }) {
    if (!router) {
      throw new Error('AxiosPlugin requires a router instance to be provided');
    }

    if (!Dependencies) {
      throw new Error('AxiosPlugin requires a Dependencies instance to be provided');
    }

    const interceptorConfig = {
      GLOBAL_STAGE,
      API_BASE_URL,
      API_BASE_URLS,
      API_FEEDS_URL,
      API_FEEDS_URLS,
    };

    configureAxiosInstance(axiosInstance, interceptorConfig, router);

    // A separate Axios instance is used by the shared {@link useApi} composable
    // so that its baseURL can be set to API_BASE_URL independently of the main
    // axiosInstance used by Arya's own direct API calls.
    // @see portal-ui/projects/base-app/composables/shared/use-api.ts
    baseSharedAxios.defaults.baseURL = API_BASE_URL;
    configureAxiosInstance(baseSharedAxios, interceptorConfig, router);

    // install dependency for both Vue and vue-context-free depenency manager
    app.provide('$api', axiosInstance);
    Dependencies.provide('$api', axiosInstance);
    Dependencies.provide('$baseApi', baseSharedAxios);
    return axiosInstance;
  }
}

export default AxiosPlugin;
